import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useApi } from "../utils/Api";

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 800;

async function fetchWithRetry(fetchFn, script, retries = MAX_RETRIES) {
  for (let i = 0; i < retries; i++) {
    try {
      const data = await fetchFn(script);
      return data;
    } catch (err) {
      if (i === retries - 1) throw err;
      await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * (i + 1)));
    }
  }
}

const MenuPanel = ({
  cartItems,
  isInCart,
  setSelectedItem,
  restaurantOpen = true,
  loadingRestaurantStatus = false,
}) => {
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false); // ← new
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [scrollingToCategory, setScrollingToCategory] = useState(false);

  const tabsRef = useRef(null);
  const tabRefs = useRef({});
  const categoryRefs = useRef({});

  const { apiFetch } = useApi();

  const Spinner = () => (
    <div className="spinner-overlay">
      <div className="spinner"></div>
    </div>
  );

  // ── Fetch with retry, independent fallbacks ──────────────────────────
  const loadMenu = useCallback(async () => {
    setIsLoading(true);
    setFetchError(false);

    const [categoriesResult, menuItemsResult] = await Promise.allSettled([
      fetchWithRetry(apiFetch, "categories"),
      fetchWithRetry(apiFetch, "menu-items"),
    ]);

    const cats =
      categoriesResult.status === "fulfilled" &&
      Array.isArray(categoriesResult.value)
        ? categoriesResult.value
        : [];
    const items =
      menuItemsResult.status === "fulfilled" &&
      Array.isArray(menuItemsResult.value)
        ? menuItemsResult.value
        : [];

    if (cats.length === 0 && items.length === 0) {
      setFetchError(true);
    }

    setCategories(cats);
    setMenuItems(items);
    setIsLoading(false);
  }, [apiFetch]);

  useEffect(() => {
    loadMenu();
  }, [loadMenu]);

  // ── Filtering ─────────────────────────────────────────────────────────
  const getMenuItemsForCategory = useCallback(
    (id) =>
      menuItems.filter((item) => {
        if (Number(item.category_id) !== Number(id)) return false;
        if (loadingRestaurantStatus || restaurantOpen) return true;
        return Number(item.category_id) === 6;
      }),
    [menuItems, loadingRestaurantStatus, restaurantOpen],
  );

  const visibleCategories = useMemo(
    () =>
      (categories || []).filter((cat) => {
        if (loadingRestaurantStatus || restaurantOpen) return true;
        return Number(cat.id) === 6;
      }),
    [categories, loadingRestaurantStatus, restaurantOpen],
  );

  const filteredCategories = useMemo(
    () =>
      visibleCategories
        .map((cat) => {
          const parentItems = getMenuItemsForCategory(cat.id).filter((item) =>
            (item.item_name || "")
              .toLowerCase()
              .includes(searchTerm.toLowerCase()),
          );
          const subcategoriesWithItems = (cat.subcategories || [])
            .filter((sub) => {
              if (loadingRestaurantStatus || restaurantOpen) return true;
              return Number(sub.id) === 6;
            })
            .map((sub) => ({
              ...sub,
              filteredItems: getMenuItemsForCategory(sub.id).filter((item) =>
                (item.item_name || "")
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase()),
              ),
            }))
            .filter((sub) => sub.filteredItems.length > 0);

          const totalItemsCount =
            parentItems.length +
            subcategoriesWithItems.reduce(
              (acc, sub) => acc + sub.filteredItems.length,
              0,
            );

          if (searchTerm && totalItemsCount === 0) return null;
          return {
            ...cat,
            parentItems,
            subcategoriesWithItems,
            totalItemsCount,
          };
        })
        .filter(Boolean),
    [
      visibleCategories,
      getMenuItemsForCategory,
      searchTerm,
      loadingRestaurantStatus,
      restaurantOpen,
    ],
  );

  useEffect(() => {
    setActiveCategory((prev) => {
      const ids = filteredCategories.map((c) => c.id);
      if (prev && ids.includes(prev)) return prev;
      return ids[0] ?? null;
    });
  // filteredCategories intentionally excluded — it's a derived value that changes
  // every render. Including it causes an infinite loop. This effect should only
  // reset active category when the user's filter intent changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, restaurantOpen, loadingRestaurantStatus]);

  // ── Scroll handlers (unchanged) ───────────────────────────────────────
  const scrollToCategory = useCallback((categoryId) => {
    setActiveCategory(categoryId);
    const el = categoryRefs.current[categoryId];
    if (!el) return;
    const offsetPosition =
      window.scrollY + el.getBoundingClientRect().top - 160;
    setScrollingToCategory(true);
    window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    setTimeout(() => setScrollingToCategory(false), 600);
  }, []);

  const updateActiveCategoryFromScroll = useCallback(() => {
    if (scrollingToCategory) return;
    const scrollPos = window.scrollY + 160;
    for (let category of filteredCategories) {
      const el = categoryRefs.current[category.id];
      if (!el) continue;
      const top = el.offsetTop;
      if (scrollPos >= top && scrollPos < top + el.offsetHeight) {
        setActiveCategory(category.id);
        break;
      }
    }
  }, [filteredCategories, scrollingToCategory]);

  useEffect(() => {
    if (filteredCategories.length === 0) return;
    updateActiveCategoryFromScroll();
    window.addEventListener("scroll", updateActiveCategoryFromScroll);
    return () =>
      window.removeEventListener("scroll", updateActiveCategoryFromScroll);
  }, [filteredCategories, updateActiveCategoryFromScroll]);

  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;
    const checkScroll = () => {
      setShowLeftArrow(el.scrollLeft > 0);
      setShowRightArrow(el.scrollWidth > el.clientWidth + el.scrollLeft + 1);
    };
    checkScroll();
    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [filteredCategories, searchTerm]);

  useEffect(() => {
    if (!activeCategory) return;
    const tabEl = tabRefs.current[activeCategory];
    const container = tabsRef.current;
    if (tabEl && container) {
      if (window.innerWidth <= 900) {
        // Mobile vertical scroll
        const parentContainer = document.querySelector('.sticky-category-tabs');
        if (parentContainer) {
          const { offsetTop: tabTop, clientHeight: tabHeight } = tabEl;
          const containerHeight = parentContainer.clientHeight;
          parentContainer.scrollTo({
            top: tabTop - containerHeight / 2 + tabHeight / 2,
            behavior: "smooth",
          });
        }
        return;
      }
      
      // Desktop horizontal scroll
      const { offsetLeft: tabLeft, clientWidth: tabWidth } = tabEl;
      const containerWidth = container.clientWidth;
      if (
        tabLeft < container.scrollLeft ||
        tabLeft + tabWidth > container.scrollLeft + containerWidth
      ) {
        container.scrollTo({
          left: tabLeft - containerWidth / 2 + tabWidth / 2,
          behavior: "smooth",
        });
      }
    }
  }, [activeCategory]);

  const scrollLeft = () =>
    tabsRef.current?.scrollBy({ left: -150, behavior: "smooth" });
  const scrollRight = () =>
    tabsRef.current?.scrollBy({ left: 150, behavior: "smooth" });

  // ── renderMenuItem unchanged ──────────────────────────────────────────
  const renderMenuItem = (item) => {
    const inStock = Number(item.in_stock) === 1;
    return (
      <div
        key={item.id}
        className={`menu-item ${isInCart(item.id) ? "in-cart" : ""} ${!inStock ? "out-of-stock" : ""}`}
        onClick={() => inStock && setSelectedItem(item)}
      >
        <div className="menu-item-top">
          <div className="menu-item-top-left">
            <h3 className="menu-item-name">{item.item_name}</h3>
            {!inStock && (
              <span className="out-of-stock-ticker">Out of Stock</span>
            )}
          </div>
          <img
            src={`https://lasgidirestaurant.com/${item.image}`}
            alt={item.item_name}
            className="menu-item-img"
            loading="lazy"
          />
        </div>
        <div className="menu-item-bottom">
          <div className="menu-item-bottom-left">
            {Number(item.price) > 0 ? (
              <p className="menu-item-price">£{item.price}</p>
            ) : (
              <p className="menu-item-price">&nbsp;</p>
            )}
          </div>
          <button className="add-btn" disabled={!inStock}>
            +
          </button>
        </div>
      </div>
    );
  };

  // ── JSX ───────────────────────────────────────────────────────────────
  return (
    <div className="menu-panel">
      {!loadingRestaurantStatus && !restaurantOpen && (
        <div className="restaurant-unavailable-banner">
          <strong>
            We're currently closed but you can place orders for next day
            collection below
          </strong>
        </div>
      )}

      <div className="menu-panel-layout">
        <div className="sticky-category-tabs">
        <div className="category-tabs-wrapper">
          {showLeftArrow && (
            <i
              className="fa fa-chevron-left scroll-arrow"
              onClick={scrollLeft}
            />
          )}
          <div className="category-tabs" ref={tabsRef}>
            {filteredCategories.map((category) => (
              <button
                key={category.id}
                ref={(el) => (tabRefs.current[category.id] = el)}
                className={`tab ${activeCategory === category.id ? "active" : ""}`}
                onClick={() => scrollToCategory(category.id)}
              >
                {category.category_name}
              </button>
            ))}
          </div>
          {showRightArrow && (
            <i
              className="fa fa-chevron-right scroll-arrow"
              onClick={scrollRight}
            />
          )}
        </div>
      </div>

      <div className="menu-container">
        <div className="menu-search-header">
          {!showSearch ? (
            <div onClick={() => setShowSearch(true)} style={{ cursor: 'pointer', padding: '8px 12px', background: '#2a2a2a', borderRadius: '50%' }}>
              <i className="fa fa-search" style={{ fontSize: '16px' }} />
            </div>
          ) : (
            <div className="search-bar" style={{ maxWidth: '400px', width: '100%', margin: 0 }}>
              <input
                type="text"
                placeholder="Search menu..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button
                className="close-search"
                onClick={() => {
                  setSearchTerm("");
                  setShowSearch(false);
                }}
              >
                <i className="fa fa-times" />
              </button>
            </div>
          )}
        </div>
        {isLoading && <Spinner />}

        {/* Error state with retry button */}
        {!isLoading && fetchError && (
          <div className="connection-error flex flex-col items-center justify-center text-center p-8 my-12 mx-auto max-w-md border border-white/10 bg-[#0c0c0c]/80 backdrop-blur-md rounded-2xl shadow-[0_8px_32px_rgba(250,147,36,0.05)] animate-fade-in-scale">
            <div className="w-16 h-16 rounded-full bg-brand-orange/10 border border-brand-orange/20 flex items-center justify-center mb-5 animate-pulse">
              <i className="fas fa-exclamation-circle text-brand-orange text-2xl" />
            </div>
            <h3 className="font-poppins text-lg font-semibold text-white mb-2 tracking-wide">
              Connection Issue
            </h3>
            <p className="text-sm text-[#bdbcbc] leading-relaxed mb-6">
              We couldn't retrieve the menu. Please check your internet
              connection and try again.
            </p>
            <button
              onClick={loadMenu}
              className="px-6 py-2.5 bg-[#fa9324] text-black font-semibold text-sm rounded-full tracking-wide transition-all duration-300 hover:bg-[#facc15] hover:shadow-[0_0_15px_rgba(250,147,36,0.4)] hover:scale-[1.03] active:scale-[0.98] cursor-pointer flex items-center gap-2"
            >
              <i className="fas fa-sync-alt text-xs animate-spin-slow" />
              Try Again
            </button>
          </div>
        )}

        {!isLoading && !fetchError && filteredCategories.length === 0 && (
          <p className="no-items">No items</p>
        )}

        {!isLoading &&
          !fetchError &&
          filteredCategories.map((category) => (
            <div
              key={category.id}
              ref={(el) => (categoryRefs.current[category.id] = el)}
              id={`category-${category.id}`}
              className="menu-category"
            >
              <h2 className="category-heading">{category.category_name}</h2>
              <div className="menu-grid">
                {category.parentItems.length > 0
                  ? category.parentItems.map(renderMenuItem)
                  : category.totalItemsCount === 0 && (
                      <p className="no-items">No items</p>
                    )}
                {category.subcategoriesWithItems.map((sub) => (
                  <div key={sub.id} className="subcategory">
                    <h3 className="subcategory-heading">{sub.category_name}</h3>
                    <div className="menu-grid">
                      {sub.filteredItems.length > 0 ? (
                        sub.filteredItems.map(renderMenuItem)
                      ) : (
                        <p className="no-items">No items</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
      </div>
      </div>
    </div>
  );
};

export default MenuPanel;
